// One app-wide WebSocket. Subscribers register handlers; auto-reconnects with
// backoff; ping keepalive. Sound on chat.message / notify (muted via localStorage).
import { useEffect } from 'react';
import { WS_URL } from '../lib/config';
import { getToken } from '../lib/auth';

let socket = null;
let handlers = new Set();
let retry = 0;
let pingTimer = null;

function playBeep() {
  if (localStorage.getItem('pms_muted') === '1') return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.25);
  } catch {
    /* no audio context available */
  }
}

function ensureSocket() {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  const token = getToken();
  if (!token) return;
  socket = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
  socket.onopen = () => {
    retry = 0;
    clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
    }, 30000);
  };
  socket.onmessage = (ev) => {
    let data;
    try {
      data = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (data.type === 'chat.message' || data.type === 'notify') playBeep();
    handlers.forEach((h) => h(data));
  };
  socket.onclose = () => {
    clearInterval(pingTimer);
    socket = null;
    if (getToken()) {
      const delay = Math.min(30000, 1000 * 2 ** retry);
      retry += 1;
      setTimeout(ensureSocket, delay);
    }
  };
}

export function closeSocket() {
  handlers = new Set();
  clearInterval(pingTimer);
  if (socket) socket.close();
  socket = null;
}

export default function useSocket(onEvent) {
  useEffect(() => {
    ensureSocket();
    if (!onEvent) return undefined;
    handlers.add(onEvent);
    return () => handlers.delete(onEvent);
  }, [onEvent]);
}
